import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/api';
import { COMPANY_NAME, LOGO_URL, PORTAL_LABEL } from '../../config/branding';
import useIsMobile from '../../utils/useIsMobile';
import { useMobileNav } from '../../context/MobileNav';
import { useFeatures } from '../../utils/useFeatures';

// Hide nav items whose feature/module is turned off (by path fragment).
const NAV_FEATURE_RULES = [
  [/\/chat$/, 'chat.enabled'],
  [/knowledge/, 'knowledge.enabled'],
  [/project/, 'projects.enabled'],
  [/(\/today|\/my-tasks|\/team-plans)/, 'plan.enabled'],
  [/\/oof/, 'oof.enabled'],
  [/(\/reports|employee-dashboard|agent-tickets|\/calendar|monthly-report)/, 'reports.enabled'],
];
function filterNavByFeatures(groups, feature) {
  return groups
    .map(g => ({ ...g, items: g.items.filter(it => {
      const rule = NAV_FEATURE_RULES.find(([re]) => re.test(it.to));
      return rule ? feature(rule[1]) : true;
    }) }))
    .filter(g => g.items.length > 0);
}

// ── Icon helper ──────────────────────────────────────────────────────────────
const ICONS = {
  grid:       '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  ticket:     '<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/>',
  users:      '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  user:       '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  building:   '<rect x="4" y="2" width="16" height="20" rx="1"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01"/>',
  chat:       '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  book:       '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
  mail:       '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
  chart:      '<path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>',
  wrench:     '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  box:        '<path d="M21 8 12 3 3 8v8l9 5 9-5V8Z"/><path d="m3 8 9 5 9-5M12 13v8"/>',
  login:      '<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>',
  shield:     '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>',
  globe:      '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
  calendar:   '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
};
function Icon({ name, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: ICONS[name] || ICONS.grid }} />
  );
}

// ── Sidebar nav structure for admin ─────────────────────────────────────────
const ADMIN_NAV = [
  {
    section: 'Overview',
    items: [
      { to: '/admin',                    label: 'Dashboard',       icon: 'grid',     end: true },
      { to: '/admin/calendar',           label: 'Calendar',        icon: 'calendar' },
      { to: '/admin/monthly-report',     label: 'Monthly Report',  icon: 'chart' },
      { to: '/admin/today',              label: "Today's Plan",    icon: 'calendar' },
      { to: '/admin/my-tasks',           label: 'My Tasks',        icon: 'book' },
      { to: '/admin/team-plans',         label: 'Team Plans',      icon: 'calendar' },
    ],
  },
  {
    section: 'Tickets',
    items: [
      { to: '/admin/tickets', label: 'All Tickets', icon: 'ticket' },
    ],
  },
  {
    section: 'Customers',
    items: [
      { to: '/admin/customers',       label: 'Customers',    icon: 'building' },
      { to: '/admin/customer-logins', label: 'Logins',       icon: 'login' },
    ],
  },
  {
    section: 'Users',
    items: [
      { to: '/admin/users', label: 'Support Users', icon: 'users' },
      { to: '/admin/oof',   label: 'OOF Manager',   icon: 'calendar' },
    ],
  },
  {
    section: 'Products',
    items: [
      { to: '/admin/products', label: 'Products', icon: 'box' },
    ],
  },
  {
    section: 'Communication',
    items: [
      { to: '/admin/chat',             label: 'Live Chat',     icon: 'chat' },
    ],
  },
  {
    section: 'Knowledge',
    items: [
      { to: '/admin/knowledge', label: 'Knowledge Base & FAQ', icon: 'book' },
    ],
  },
  {
    section: 'Projects',
    items: [
      { to: '/admin/project-dashboard', label: 'Dashboard',     icon: 'chart' },
      { to: '/admin/projects',          label: 'All Projects', icon: 'box' },
      { to: '/admin/project-templates', label: 'Templates',    icon: 'book' },
    ],
  },
  {
    section: 'System',
    items: [
      { to: '/admin/maintenance', label: 'Maintenance', icon: 'wrench' },
    ],
  },
];

const MANAGER_NAV = [
  {
    section: 'Overview',
    items: [
      { to: '/manager',                    label: 'Dashboard',     icon: 'grid', end: true },
      { to: '/manager/calendar',           label: 'Calendar',      icon: 'calendar' },
      { to: '/manager/monthly-report',     label: 'Monthly Report', icon: 'chart' },
      { to: '/manager/team-plans',         label: 'Team Plans',    icon: 'calendar' },
    ],
  },
  {
    section: 'Tickets',
    items: [
      { to: '/manager/today',    label: "Today's Plan", icon: 'calendar' },
      { to: '/manager/my-tasks', label: 'My Tasks',     icon: 'book' },
      { to: '/manager/tickets',  label: 'All Tickets', icon: 'ticket' },
    ],
  },
  {
    section: 'Communication',
    items: [
      { to: '/manager/chat',             label: 'Live Chat',     icon: 'chat' },
    ],
  },
  {
    section: 'Knowledge',
    items: [
      { to: '/manager/knowledge', label: 'Knowledge Base & FAQ', icon: 'book' },
    ],
  },
  {
    section: 'Workforce',
    items: [
      { to: '/manager/oof', label: 'OOF Manager', icon: 'calendar' },
    ],
  },
  {
    section: 'Projects',
    items: [
      { to: '/admin/project-dashboard', label: 'Dashboard',   icon: 'chart' },
      { to: '/admin/projects',          label: 'All Projects', icon: 'box' },
      { to: '/admin/project-templates', label: 'Templates',    icon: 'book' },
    ],
  },
];


// ── Theme toggle ─────────────────────────────────────────────────────────────
function useTheme() {
  const [theme, setThemeState] = useState(() => localStorage.getItem('kg_theme') || 'light');
  function setTheme(t) {
    setThemeState(t);
    localStorage.setItem('kg_theme', t);
    document.body.classList.toggle('dark', t === 'dark');
  }
  React.useEffect(() => {
    document.body.classList.toggle('dark', theme === 'dark');
  }, []);
  return [theme, setTheme];
}

// ── Sidebar (admin / manager) ─────────────────────────────────────────────────
function Sidebar({ navGroups, user, onLogout, onChangePwd, isMobile, open, onClose }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState({});
  const [logoAnim, setLogoAnim] = useState(false);
  const [theme, setTheme] = useTheme();
  // On mobile the drawer is always full (never "mini")
  const [miniState, setMini] = useState(() => localStorage.getItem('kg_sidebar_mini') === '1');
  const mini = isMobile ? false : miniState;
  const toggle = (s) => setCollapsed(c => ({ ...c, [s]: !c[s] }));
  function toggleMini() {
    setMini(m => { const n = !m; localStorage.setItem('kg_sidebar_mini', n ? '1' : '0'); return n; });
  }

  function handleLogoClick() {
    setLogoAnim(false);
    requestAnimationFrame(() => requestAnimationFrame(() => setLogoAnim(true)));
    setTimeout(() => setLogoAnim(false), 600);
  }

  function isActive(to, end) {
    if (end) return location.pathname === to;
    return location.pathname.startsWith(to);
  }

  const asideStyle = isMobile
    ? {
        width: 260, background: 'var(--bg-sidebar)', color: '#fff',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 1001,
        overflowY: 'auto', overflowX: 'hidden',
        paddingTop: 'env(safe-area-inset-top)',
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform .22s ease', boxShadow: open ? '2px 0 18px rgba(0,0,0,.35)' : 'none',
      }
    : {
        width: mini ? 66 : 220, minHeight: '100vh', background: 'var(--bg-sidebar)', color: '#fff',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        position: 'sticky', top: 0, height: '100vh',
        overflowY: 'auto', overflowX: 'hidden', transition: 'width .16s ease',
      };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && open && (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000 }} />
      )}
    <aside style={asideStyle}>
      {/* Collapse toggle: on desktop toggles mini; on mobile closes the drawer */}
      <button onClick={isMobile ? onClose : toggleMini} title={isMobile ? 'Close menu' : (mini ? 'Expand menu' : 'Collapse menu')}
        style={{ position: 'sticky', top: 0, alignSelf: (mini && !isMobile) ? 'center' : 'flex-end',
                 margin: (mini && !isMobile) ? '8px 0 0' : '8px 8px 0', zIndex: 5,
                 width: 28, height: 28, borderRadius: 7, cursor: 'pointer',
                 border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.08)',
                 color: 'rgba(255,255,255,.8)', fontSize: 14, lineHeight: 1, flexShrink: 0 }}>
        {isMobile ? '✕' : (mini ? '»' : '«')}
      </button>
      {/* Logo */}
      <style>{`
        @keyframes kg-logo-zoom {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.18); }
          70%  { transform: scale(0.96); }
          100% { transform: scale(1); }
        }
        .kg-logo-zoom { animation: kg-logo-zoom 0.55s cubic-bezier(.36,.07,.19,.97); }
      `}</style>
      <div style={{ padding: mini ? '8px 8px 12px' : '10px 18px 14px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
        <div
          onClick={handleLogoClick}
          style={{ background: 'var(--bg-surface)', borderRadius: 10, padding: mini ? '6px' : '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <img
            src={LOGO_URL}
            alt={COMPANY_NAME}
            className={logoAnim ? 'kg-logo-zoom' : ''}
            style={{ height: mini ? 34 : 64, objectFit: 'contain', display: 'block' }}
          />
        </div>
        {!mini && <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginTop: 8, letterSpacing: .5, textAlign: 'center', fontWeight: 500 }}>{PORTAL_LABEL}</div>}
      </div>

      {/* Nav sections */}
      <nav style={{ flex: 1, paddingTop: 8 }}>
        {navGroups.map((grp, gi) => {
          const isCol = !!collapsed[grp.section];
          // ── Collapsed (mini) mode: icon-only, no section headers ──
          if (mini) {
            return (
              <div key={grp.section} style={{ paddingBottom: 4, borderTop: gi > 0 ? '1px solid rgba(255,255,255,.07)' : 'none', marginTop: gi > 0 ? 4 : 0, paddingTop: gi > 0 ? 4 : 0 }}>
                {grp.items.map((it) => {
                  const active = isActive(it.to, it.end);
                  return (
                    <Link key={it.to} to={it.to} title={it.label} onClick={() => isMobile && onClose && onClose()}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                               padding: '10px 0', color: active ? '#fff' : 'rgba(255,255,255,.75)',
                               background: active ? 'rgba(255,255,255,.12)' : 'transparent',
                               borderLeft: `3px solid ${active ? '#60a5fa' : 'transparent'}`,
                               textDecoration: 'none', transition: 'all .12s' }}
                      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,.07)'; e.currentTarget.style.color = '#fff'; } }}
                      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,.75)'; } }}>
                      <Icon name={it.icon} size={19} />
                    </Link>
                  );
                })}
              </div>
            );
          }
          // ── Expanded mode: collapsible sections with labels ──
          return (
            <div key={grp.section}>
              <button onClick={() => toggle(grp.section)} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 18px 4px',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 700, letterSpacing: 1,
                color: 'rgba(255,255,255,.45)', textTransform: 'uppercase',
              }}>
                <span>{grp.section}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,.5)', lineHeight: 1 }}>
                  {isCol ? '+' : '−'}
                </span>
              </button>
              {!isCol && (
                <div style={{ paddingBottom: 4 }}>
                  {grp.items.map((it) => {
                    const active = isActive(it.to, it.end);
                    return (
                      <Link key={it.to} to={it.to} onClick={() => isMobile && onClose && onClose()} style={{
                        display: 'flex', alignItems: 'center', gap: 9,
                        padding: '8px 18px',
                        fontSize: 14,
                        fontWeight: 700,
                        color: active ? '#fff' : 'rgba(255,255,255,.75)',
                        background: active ? 'rgba(255,255,255,.12)' : 'transparent',
                        borderLeft: `3px solid ${active ? '#60a5fa' : 'transparent'}`,
                        textDecoration: 'none',
                        transition: 'all .12s',
                      }}
                      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,.07)'; e.currentTarget.style.color = '#fff'; } }}
                      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,.75)'; } }}
                      >
                        <Icon name={it.icon} size={15} />
                        <span>{it.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Theme toggle at bottom */}
      {mini ? (
        <div style={{ borderTop: '1px solid rgba(255,255,255,.1)', padding: '10px 0', display: 'flex', justifyContent: 'center' }}>
          <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            style={{ width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
                     border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)',
                     color: '#60a5fa', fontSize: 16 }}>
            {theme === 'dark' ? '☀' : '🌙'}
          </button>
        </div>
      ) : (
      <div style={{ borderTop: '1px solid rgba(255,255,255,.1)', padding: '14px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 10px', background: 'rgba(255,255,255,.06)', borderRadius: 8 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5 }}>Theme</span>
          <div style={{ display: 'flex', gap: 10, marginLeft: 'auto' }}>
            {[{ val: 'light', label: '☀ Light' }, { val: 'dark', label: '🌙 Dark' }].map(({ val, label }) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, color: theme === val ? '#60a5fa' : 'rgba(255,255,255,.55)', fontWeight: theme === val ? 700 : 400 }}>
                <input type="radio" name="kg_theme" value={val} checked={theme === val} onChange={() => setTheme(val)}
                  style={{ accentColor: '#60a5fa', cursor: 'pointer', width: 12, height: 12 }} />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>
      )}
    </aside>
    </>
  );
}

// ── Top-right user dropdown ───────────────────────────────────────────────────
export function TopUserMenu({ user, onLogout, onChangePwd }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const ROLE_LABEL = { admin: 'Administrator', super_admin: 'Super Admin', manager: 'Manager', agent: 'Agent', customer: 'Customer', customer_manager: 'Cust. Manager' };

  React.useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px 6px 6px', borderRadius: 999,
        border: '1.5px solid var(--border-subtle)',
        background: 'var(--bg-surface)', cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(15,23,42,.06)',
      }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#0B6623,#19a843)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
          {user?.name?.[0]?.toUpperCase()}
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{user?.name}</div>
          <div style={{ fontSize: 10.5, fontWeight: 600, color: '#0B6623', textTransform: 'uppercase', letterSpacing: .4 }}>{ROLE_LABEL[user?.role] || user?.role}</div>
        </div>
        <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 2 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: 240, background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)', borderRadius: 12,
          boxShadow: '0 10px 25px rgba(15,23,42,.12)', zIndex: 200, overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{user?.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{user?.email}</div>
            <div style={{ marginTop: 8, display: 'inline-block', fontSize: 11, fontWeight: 700, background: 'var(--action-bg)', color: '#0B6623', borderRadius: 999, padding: '2px 10px', textTransform: 'uppercase', letterSpacing: .5 }}>
              {ROLE_LABEL[user?.role] || user?.role}
            </div>
          </div>
          <div style={{ padding: '6px 0' }}>
            <button onClick={() => { setOpen(false); onChangePwd(); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              🔑 Change Password
            </button>
            <button onClick={() => { setOpen(false); onLogout(); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              → Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Top nav (agent / customer / others) ─────────────────────────────────────
function TopNav({ links, user, onLogout, onChangePwd }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  function isActive(to) {
    if (to === '/admin' || to === '/tickets') return location.pathname === to;
    return location.pathname.startsWith(to);
  }

  return (
    <>
      <div style={{ height: 3, background: 'linear-gradient(90deg,#0B6623 0%,#19a843 60%,#86efac 100%)', position: 'sticky', top: 0, zIndex: 101 }} />
      <nav style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)', position: 'sticky', top: 3, zIndex: 100, boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 16px', display: 'flex', alignItems: 'center', height: 64, gap: 0 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginRight: 16 }}>
            <img src={LOGO_URL} alt={COMPANY_NAME} className="nav-logo" style={{ height: 108, objectFit: 'contain', marginTop: -22, marginBottom: -22 }} />
          </Link>
          <div className="nav-divider" style={{ width: 1, height: 32, background: 'var(--border-subtle)', marginRight: 16, flexShrink: 0 }} />

          <div className="nav-links-desktop" style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, overflowX: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
            {links.map(({ to, label }) => {
              const active = isActive(to);
              return (
                <Link key={to} to={to} style={{
                  display: 'inline-flex', alignItems: 'center', padding: '6px 14px', borderRadius: 9999,
                  fontSize: 13, fontWeight: active ? 700 : 500,
                  color: active ? '#fff' : '#475569',
                  background: active ? 'linear-gradient(135deg,#0B6623 0%,#0d7a28 100%)' : 'transparent',
                  boxShadow: active ? '0 2px 6px rgba(21,128,61,.3)' : 'none',
                  textDecoration: 'none', whiteSpace: 'nowrap', transition: 'all .15s',
                  border: active ? '1.5px solid #083d18' : '1.5px solid transparent',
                }}>{label}</Link>
              );
            })}
          </div>

          <div className="nav-user-desktop" style={{ paddingLeft: 16, borderLeft: '1px solid var(--border-subtle)', marginLeft: 12 }}>
            <TopUserMenu user={user} onLogout={onLogout} onChangePwd={onChangePwd} />
          </div>

          <button className="nav-hamburger" onClick={() => setMobileOpen(o => !o)} style={{ display: 'none', marginLeft: 'auto', background: 'none', border: '1.5px solid var(--border-subtle)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', flexDirection: 'column', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ display: 'block', width: 18, height: 2, background: '#475569', borderRadius: 2, transition: 'all .2s', transform: mobileOpen ? 'rotate(45deg) translate(4px,4px)' : 'none' }} />
            <span style={{ display: 'block', width: 18, height: 2, background: '#475569', borderRadius: 2, transition: 'all .2s', opacity: mobileOpen ? 0 : 1 }} />
            <span style={{ display: 'block', width: 18, height: 2, background: '#475569', borderRadius: 2, transition: 'all .2s', transform: mobileOpen ? 'rotate(-45deg) translate(4px,-4px)' : 'none' }} />
          </button>
        </div>

        {mobileOpen && (
          <div style={{ borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', padding: '12px 16px 16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {links.map(({ to, label }) => {
                const active = isActive(to);
                return (
                  <Link key={to} to={to} onClick={() => setMobileOpen(false)} style={{ padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: active ? 700 : 500, color: active ? '#fff' : 'var(--text-muted)', background: active ? 'linear-gradient(135deg,#0B6623,#0d7a28)' : 'var(--bg-app)', textDecoration: 'none', border: `1px solid ${active ? '#083d18' : 'var(--border-subtle)'}` }}>{label}</Link>
                );
              })}
            </div>
            <button onClick={onLogout} style={{ marginTop: 12, width: '100%', padding: '10px', background: 'var(--bg-surface)', border: '1.5px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Sign Out</button>
          </div>
        )}
      </nav>
      <style>{`
        @media (max-width: 768px) {
          .nav-links-desktop { display: none !important; }
          .nav-user-desktop  { display: none !important; }
          .nav-divider       { display: none !important; }
          .nav-hamburger     { display: flex !important; }
          .nav-logo { height: 48px !important; margin-top: 0 !important; margin-bottom: 0 !important; }
        }
      `}</style>
    </>
  );
}

// ── Change Password Modal ─────────────────────────────────────────────────────
export function ChangePwdModal({ onClose }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    if (form.new_password !== form.confirm_password) return setError('New passwords do not match');
    if (form.new_password.length < 6) return setError('New password must be at least 6 characters');
    setLoading(true);
    try {
      await apiFetch('/auth/change-password', { method: 'POST', body: { current_password: form.current_password, new_password: form.new_password } });
      setSuccess('Password changed successfully!');
      setTimeout(() => onClose(), 2000);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Change Password</h3>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>Update your account password</p>
        {error   && <div style={{ background: 'var(--red-50)', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>{error}</div>}
        {success && <div style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: 'var(--status-resolved-text)' }}>{success}</div>}
        <form onSubmit={handleSubmit}>
          {[['current_password','Current Password'],['new_password','New Password'],['confirm_password','Confirm New Password']].map(([key, label]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>{label}</label>
              <input type="password" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} required style={{ width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13, border: '1.5px solid var(--border-subtle)', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1.5px solid var(--border-subtle)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#0B6623', fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: loading ? .7 : 1 }}>{loading ? 'Saving...' : 'Change Password'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const AGENT_NAV = [
  {
    section: 'Tickets',
    items: [
      { to: '/agent/today',    label: "Today's Plan", icon: 'calendar' },
      { to: '/agent/my-tasks', label: 'My Tasks',     icon: 'book' },
      { to: '/agent/tickets',  label: 'My Tickets', icon: 'ticket' },
    ],
  },
  {
    section: 'Communication',
    items: [
      { to: '/agent/chat', label: 'Live Chat', icon: 'chat' },
    ],
  },
  {
    section: 'Projects',
    items: [
      { to: '/admin/project-dashboard', label: 'My Dashboard', icon: 'chart' },
      { to: '/admin/projects',          label: 'All Projects', icon: 'box' },
      { to: '/admin/project-templates', label: 'Templates',    icon: 'book' },
    ],
  },
  {
    section: 'Knowledge',
    items: [
      { to: '/agent/knowledge', label: 'Knowledge Base & FAQ', icon: 'book' },
    ],
  },
];

const CUSTOMER_NAV = [
  {
    section: 'Tickets',
    items: [
      { to: '/tickets',     label: 'My Tickets',  icon: 'ticket', end: true },
      { to: '/tickets/new', label: 'New Ticket',   icon: 'globe' },
    ],
  },
  {
    section: 'Communication',
    items: [
      { to: '/chat', label: 'Live Chat', icon: 'chat' },
    ],
  },
];

const CUSTOMER_MANAGER_NAV = [
  {
    section: 'Tickets',
    items: [
      { to: '/customer-manager/tickets', label: "My Company's Tickets", icon: 'ticket' },
    ],
  },
];

// ── Main Navbar export ────────────────────────────────────────────────────────
export default function Navbar() {
  const { user, logout, isAdmin, isManager } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { drawerOpen, setDrawerOpen } = useMobileNav();
  const { feature } = useFeatures();
  const [showChangePwd, setShowChangePwd] = useState(false);

  function handleLogout() { logout(); nav('/login'); }

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname, setDrawerOpen]);

  if (!user) return null;

  const baseNav = isAdmin ? ADMIN_NAV
    : isManager               ? MANAGER_NAV
    : user.role === 'agent'   ? AGENT_NAV
    : user.role === 'customer_manager' ? CUSTOMER_MANAGER_NAV
    : CUSTOMER_NAV;
  const navGroups = filterNavByFeatures(baseNav, feature);

  return (
    <>
      <Sidebar navGroups={navGroups} user={user} onLogout={handleLogout} onChangePwd={() => setShowChangePwd(true)}
        isMobile={isMobile} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      {showChangePwd && <ChangePwdModal onClose={() => setShowChangePwd(false)} />}
    </>
  );
}
